import React, { useMemo, useState, Fragment } from 'react';
import { createPortal } from 'react-dom';
import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react';
import { AlertCircle, ChevronDown, Check } from 'lucide-react';
import { WorkTypeCategory, WorkType } from 'types/project/projectDefaults';
import { workTypeUIMap } from '../modules/ProjectManagement/Dropdowns/TaskTypeDropdown';
import { VALID_PARENT_CATEGORIES, HIERARCHY_RULES } from 'utils/helpers';
import { LoaderButton } from 'components/common/LoadingButton';

interface ConvertWorkItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  workItemKey: string;
  workItemTitle: string;
  currentWorkTypeCategory: WorkTypeCategory;
  currentParentUuid: string | null;
  currentParentCategory?: WorkTypeCategory;
  availableWorkTypes: WorkType[];
  onSave: (newWorkTypeUuid: string, parentAction: 'remove' | 'retain') => Promise<boolean>;
  isSaving?: boolean;
  hasChildren?: boolean;
}

const ConvertWorkItemModal: React.FC<ConvertWorkItemModalProps> = ({
  isOpen,
  onClose,
  workItemKey,
  workItemTitle,
  currentWorkTypeCategory,
  currentParentUuid,
  currentParentCategory,
  availableWorkTypes,
  onSave,
  isSaving = false,
  hasChildren = false,
}) => {
  const [selectedType, setSelectedType] = useState<WorkType | null>(null);

  // Filter allowed target types based on matrix logic
  // - Sub-task -> Task, Bug, Story, Epic
  // - Task -> Bug, Story, Epic
  // - Bug -> Task, Story, Epic
  // - Story -> Epic
  // - Epic -> Cannot be converted
  const allowedTargetCategories = useMemo(() => {
    switch (currentWorkTypeCategory) {
      case WorkTypeCategory.SUB_TASK:
        return [
          WorkTypeCategory.TASK,
          WorkTypeCategory.BUG,
          WorkTypeCategory.STORY,
          WorkTypeCategory.EPIC,
        ];
      case WorkTypeCategory.TASK:
        return [WorkTypeCategory.BUG, WorkTypeCategory.STORY, WorkTypeCategory.EPIC];
      case WorkTypeCategory.BUG:
        return [WorkTypeCategory.TASK, WorkTypeCategory.STORY, WorkTypeCategory.EPIC];
      case WorkTypeCategory.STORY:
        return [WorkTypeCategory.EPIC];
      case WorkTypeCategory.EPIC:
      default:
        return [];
    }
  }, [currentWorkTypeCategory]);

  const candidateWorkTypes = useMemo(() => {
    return availableWorkTypes.filter((wt) =>
      allowedTargetCategories.includes(wt.workTypeCategory)
    );
  }, [availableWorkTypes, allowedTargetCategories]);

  const formatCategory = (category: string) => {
    if (!category) return '';
    const cat = category.toUpperCase();
    if (cat === 'SUB_TASK') return 'Sub-task';
    if (cat === 'USER_STORY') return 'User Story';
    return cat.charAt(0) + cat.slice(1).toLowerCase();
  };

  const checkIsDisabled = (targetCategory: WorkTypeCategory) => {
    if (!hasChildren) return false;
    
    const currentAllowedChildren = HIERARCHY_RULES[currentWorkTypeCategory] || [];
    if (currentAllowedChildren.length === 0) return false;

    const targetAllowedChildren = HIERARCHY_RULES[targetCategory] || [];
    
    return !currentAllowedChildren.every(c => targetAllowedChildren.includes(c));
  };

  const getParentAction = (targetCategory: WorkTypeCategory) => {
    if (!currentParentUuid) return 'None (Already a standalone item)';

    if (currentWorkTypeCategory === WorkTypeCategory.SUB_TASK) {
      return 'Will be removed'; // Sub-task promotion always removes mandatory parent
    }

    const allowedParentsForTarget = VALID_PARENT_CATEGORIES[targetCategory] || [];
    
    if (allowedParentsForTarget.length === 0) {
      return 'Will be removed';
    }

    if (currentParentCategory) {
      if (allowedParentsForTarget.includes(currentParentCategory)) {
        return 'Will be retained';
      } else {
        return 'Will be removed';
      }
    }

    // Fallback if we don't know parent category but it has a parent
    return 'Will be evaluated and potentially removed';
  };

  const handleConfirmSave = async () => {
    if (!selectedType) return;
    const action = getParentAction(selectedType.workTypeCategory as WorkTypeCategory) === 'Will be removed' ? 'remove' : 'retain';
    const success = await onSave(selectedType.workTypeUuid, action);
    if (success) {
      onClose();
    }
  };

  React.useEffect(() => {
    if (!isOpen) {
      setSelectedType(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <Transition show={isOpen} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-[777]">
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </TransitionChild>

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95 translate-y-4"
            enterTo="opacity-100 scale-100 translate-y-0"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100 translate-y-0"
            leaveTo="opacity-0 scale-95 translate-y-4"
          >
            <DialogPanel className="w-full max-w-lg transform rounded-2xl bg-[#1c2030] border border-[#2d3142]/40 shadow-2xl transition-all text-left overflow-visible">
              {/* Header */}
              <div className="px-6 pt-6 pb-4 border-b border-[#2d3142]/40">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-bold text-white tracking-wide">
                      Convert Work Item Type
                    </h3>
                    <p className="mt-1 text-xs text-[#8e94a5] font-medium leading-relaxed">
                      {workItemKey} &middot; {workItemTitle}
                    </p>
                  </div>
                </div>
              </div>

              {/* Body */}
              <div className="p-6 space-y-6 relative">
                {selectedType && (() => {
                  const hasParent = !!currentParentUuid || currentWorkTypeCategory === WorkTypeCategory.SUB_TASK;
                  const parentAction = getParentAction(selectedType.workTypeCategory as WorkTypeCategory);
                  const willRemove = currentWorkTypeCategory === WorkTypeCategory.SUB_TASK || parentAction.includes('removed');
                  const parentLabel = currentParentCategory ? formatCategory(currentParentCategory) : 'parent';
                  const currentTypeName = formatCategory(currentWorkTypeCategory);
                  const newTypeName = selectedType.name || formatCategory(selectedType.workTypeCategory);

                  let message: string;
                  if (!hasParent) {
                    message = `This item has no parent. It will be converted from a ${currentTypeName} to a ${newTypeName} and will remain standalone — no relationships will be affected.`;
                  } else if (willRemove) {
                    message = `Converting this item will remove it from its current parent. It will become a standalone ${newTypeName} with no parent relationship. All existing data will be preserved.`;
                  } else {
                    message = `This item will remain a child of its ${parentLabel} after conversion. The parent-child relationship is fully preserved — only the work item type changes from ${currentTypeName} to ${newTypeName}.`;
                  }

                  return (
                    <div className="flex gap-3 bg-[#8B5CF61A] border border-[#8b5cf6]/30 rounded-xl p-4 text-xs items-start">
                      <AlertCircle className="w-4.5 h-4.5 text-[#8B5CF6] shrink-0" />
                      <p className="text-[#9BA5C4] leading-relaxed">{message}</p>
                    </div>
                  );
                })()}

                <div className="space-y-2">
                  <label className="block text-[11px] font-bold text-[#8e94a5] uppercase tracking-wider">
                    Current Type
                  </label>
                  <div className="bg-[#121420] border border-[#2d3142]/60 rounded-xl px-4 py-3 flex items-center justify-between min-h-[50px] opacity-70">
                    <div className="flex items-center gap-2">
                      {(() => {
                        const ui = workTypeUIMap[currentWorkTypeCategory as keyof typeof workTypeUIMap];
                        return ui && <span className={`${ui.color} shrink-0 text-sm`}>{ui.icon}</span>;
                      })()}
                      <span className="text-sm font-medium text-white">
                        {formatCategory(currentWorkTypeCategory)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-[11px] font-bold text-[#8e94a5] uppercase tracking-wider">
                    Convert To
                  </label>
                  <Listbox value={selectedType} onChange={setSelectedType}>
                    <div className="relative mt-1">
                      <ListboxButton className="relative w-full bg-[#121420] border border-[#2d3142]/60 hover:border-[#8b5cf6]/40 rounded-xl px-4 py-3 flex items-center justify-between min-h-[50px] text-left cursor-default sm:text-sm transition-colors outline-none focus:ring-1 focus:ring-[#8b5cf6]/60">
                        {selectedType ? (
                          <div className="flex items-center gap-2">
                            {(() => {
                              const ui = workTypeUIMap[selectedType.workTypeCategory as keyof typeof workTypeUIMap];
                              return ui && <span className={`${ui.color} shrink-0 text-sm`}>{ui.icon}</span>;
                            })()}
                            <span className="text-sm font-medium text-white">
                              {selectedType.name || formatCategory(selectedType.workTypeCategory)}
                            </span>
                          </div>
                        ) : (
                          <span className="text-[#5c6175]">Select target type</span>
                        )}
                        <span className="pointer-events-none flex items-center pr-2">
                          <ChevronDown className="h-5 w-5 text-[#5c6175]" aria-hidden="true" />
                        </span>
                      </ListboxButton>
                      <Transition
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                      >
                        <ListboxOptions className="absolute z-[100] mt-1 max-h-60 w-full overflow-auto rounded-xl bg-[#121420] border border-[#2d3142] py-1 text-base shadow-lg focus:outline-none sm:text-sm">
                          {candidateWorkTypes.map((wt) => {
                            const ui = workTypeUIMap[wt.workTypeCategory as keyof typeof workTypeUIMap];
                            const isDisabled = checkIsDisabled(wt.workTypeCategory as WorkTypeCategory);
                            return (
                              <ListboxOption
                                key={wt.workTypeUuid}
                                disabled={isDisabled}
                                className={({ focus, disabled }) =>
                                  `relative select-none py-2 pl-4 pr-4 transition-colors ${
                                    disabled ? 'opacity-50 cursor-not-allowed' : focus ? 'bg-[#2d3142]/40 text-white cursor-pointer' : 'text-[#8e94a5] cursor-pointer'
                                  }`
                                }
                                value={wt}
                              >
                                {({ selected }) => (
                                  <div className="flex items-center justify-between">
                                    <div className="flex flex-col">
                                      <div className="flex items-center gap-2">
                                        {ui && <span className={`${ui.color} shrink-0 text-sm`}>{ui.icon}</span>}
                                        <span className={`block truncate ${selected ? 'font-medium text-white' : 'font-normal'}`}>
                                          {wt.name || formatCategory(wt.workTypeCategory)}
                                        </span>
                                      </div>
                                      {isDisabled ? (
                                        <span className="text-[10px] text-[#ff6b6b] mt-1 whitespace-normal leading-tight">
                                          Not available: Current child items are unsupported by this type.
                                        </span>
                                      ) : currentParentUuid ? (
                                        <span className={`text-[10px] mt-1 whitespace-normal leading-tight ${getParentAction(wt.workTypeCategory as WorkTypeCategory).includes('removed') ? 'text-[#ff6b6b]' : 'text-[#8e94a5]'}`}>
                                          {getParentAction(wt.workTypeCategory as WorkTypeCategory).includes('removed')
                                            ? 'Parent relationship will be removed'
                                            : `${currentParentCategory ? formatCategory(currentParentCategory) : 'Parent'} association retained`}
                                        </span>
                                      ) : null}
                                    </div>
                                    {selected ? (
                                      <span className="flex items-center text-[#8b5cf6]">
                                        <Check className="h-4 w-4" aria-hidden="true" />
                                      </span>
                                    ) : null}
                                  </div>
                                )}
                              </ListboxOption>
                            );
                          })}
                        </ListboxOptions>
                      </Transition>
                    </div>
                  </Listbox>
                </div>

                {selectedType && (
                  <div className="space-y-2">
                    <label className="block text-[11px] font-bold text-[#8e94a5] uppercase tracking-wider">
                      Compatibility Check
                    </label>
                    <div className="bg-[#121420] border border-[#2d3142]/60 rounded-xl p-4 space-y-3">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-[#5c6175]">Current Type</span>
                        <div className="flex items-center gap-1.5">
                          {(() => {
                            const ui = workTypeUIMap[currentWorkTypeCategory as keyof typeof workTypeUIMap];
                            return ui && <span className={`${ui.color} shrink-0 text-xs`}>{ui.icon}</span>;
                          })()}
                          <span className="text-white font-medium">{formatCategory(currentWorkTypeCategory)}</span>
                        </div>
                      </div>
                      
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-[#5c6175]">New Type</span>
                        <div className="flex items-center gap-1.5">
                          {(() => {
                            const ui = workTypeUIMap[selectedType.workTypeCategory as keyof typeof workTypeUIMap];
                            return ui && <span className={`${ui.color} shrink-0 text-xs`}>{ui.icon}</span>;
                          })()}
                          <span className="text-white font-medium">{formatCategory(selectedType.workTypeCategory)}</span>
                        </div>
                      </div>

                      <div className="flex justify-between items-start text-sm pt-2 border-t border-[#2d3142]/40">
                        <span className="text-[#5c6175] shrink-0 mr-4">Result</span>
                        <span className="text-white font-medium text-sm text-right">
                          {getParentAction(selectedType.workTypeCategory as WorkTypeCategory).includes('removed') || !currentParentUuid
                            ? `Standalone ${formatCategory(selectedType.workTypeCategory)}`
                            : `Child ${formatCategory(selectedType.workTypeCategory)} (${currentParentCategory ? formatCategory(currentParentCategory) : 'Parent'} association retained)`}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 w-full border-t border-[#2d3142]/40 flex items-center bg-[#181c2b] rounded-b-2xl justify-end">
                <div className="flex items-center gap-3 w-full">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={isSaving}
                    className="px-5 py-2.5 flex-1 border border-primary-subtle text-sm font-semibold text-[#8e94a5] hover:text-white transition-all disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <LoaderButton
                    className={`!m-0  flex-[2] !px-6 font-medium !text-sm  hover:shadow-[#8b5cf6]/20 disabled:opacity-50 disabled:cursor-not-allowed`}
                    text="Convert Work Item"
                    isLoading={isSaving}
                    disabled={!selectedType || isSaving}
                    onClick={handleConfirmSave}
                  />
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>,
    document.body
  );
};

export default ConvertWorkItemModal;
